import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
  Grid,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  IconButton,
  Tooltip,
  Autocomplete,
  Avatar,
  Stack
} from '@mui/material';
import {
  Search as SearchIcon,
  Refresh as RefreshIcon,
  GetApp as DownloadIcon,
  PlayArrow as PlayIcon,
  ShoppingCart as PurchaseIcon,
  CardGiftcard as RefillIcon,
  Remove as DeductIcon,
  Add as AddIcon,
  MusicNote as MusicIcon
} from '@mui/icons-material';
import ApiService from '../../services/api-service';
import notifier from '../../components/Notifier';
import { format } from 'date-fns';

export default function StreamTransactions() {
  const [loading, setLoading] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [currentUserBalance, setCurrentUserBalance] = useState(0);
  const [userSearch, setUserSearch] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [summary, setSummary] = useState(null);
  const [mostPlayed, setMostPlayed] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [totalCount, setTotalCount] = useState(0);
  const [filters, setFilters] = useState({
    transactionType: '',
    startDate: '',
    endDate: ''
  });

  // Search for users
  const searchUsers = async (query) => {
    if (!query.trim()) {
      setSearchResults([]);
      return;
    }

    setSearchLoading(true);
    try {
      const response = await ApiService.ADMIN_SEARCH_USER(query);
      console.log('User search response:', response);
      
      let users = [];
      if (response.result) {
        // Handle different response structures
        if (Array.isArray(response.result)) {
          users = response.result;
        } else if (response.result.response && Array.isArray(response.result.response)) {
          users = response.result.response;
        } else if (response.result.data && Array.isArray(response.result.data)) {
          users = response.result.data;
        } else if (response.result.users && Array.isArray(response.result.users)) {
          users = response.result.users;
        }
      } else if (response.response && Array.isArray(response.response)) {
        users = response.response;
      } else if (response.data && Array.isArray(response.data)) {
        users = response.data;
      }
      
      setSearchResults(users.slice(0, 10));
    } catch (error) {
      console.error('Error searching users:', error);
      notifier.error('Failed to search users');
    } finally {
      setSearchLoading(false);
    }
  };

  // Fetch transactions for selected user
  const fetchTransactions = async () => {
    if (!selectedUser) return;

    setLoading(true);
    try {
      const params = {
        userId: selectedUser._id,
        pageNo: page + 1,
        pageSize: rowsPerPage,
        ...filters
      };

      const response = await ApiService.ADMIN_GET_USER_STREAM_TRANSACTIONS(params);
      if (response.result) {
        setTransactions(response.result.transactions || []);
        setTotalCount(response.result.totalCount || 0);
        // Update current balance from the API response
        if (response.result.user && response.result.user.currentBalance !== undefined) {
          setCurrentUserBalance(response.result.user.currentBalance);
        }
      }
    } catch (error) {
      console.error('Error fetching transactions:', error);
      notifier.error('Failed to fetch stream transactions');
    } finally {
      setLoading(false);
    }
  };

  // Fetch summary for selected user
  const fetchSummary = async () => {
    if (!selectedUser) return;

    try {
      const params = {
        userId: selectedUser._id,
        ...filters
      };

      const response = await ApiService.ADMIN_GET_USER_STREAM_SUMMARY(params);
      if (response.result) {
        setSummary(response.result.summary || {});
        setMostPlayed(response.result.mostPlayed || []);
      }
    } catch (error) {
      console.error('Error fetching summary:', error);
    }
  };

  useEffect(() => {
    if (selectedUser && selectedUser._id) {
      fetchTransactions();
      fetchSummary();
    }
  }, [selectedUser, page, rowsPerPage, filters]);

  const handleUserSelect = (user) => {
    if (!user || !user._id) {
      notifier.error('Invalid user selected');
      return;
    }
    setSelectedUser(user);
    setCurrentUserBalance(user.streamingBalance || 0);
    setUserSearch(user.email);
    setSearchResults([]);
    setPage(0);
  };

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }));
    setPage(0);
  };

  const getTransactionIcon = (type) => {
    switch (type) {
      case 'deduction': return <DeductIcon sx={{ color: '#f44336' }} />;
      case 'purchase': return <PurchaseIcon sx={{ color: '#4caf50' }} />;
      case 'refill': return <RefillIcon sx={{ color: '#2196f3' }} />;
      case 'addition': return <AddIcon sx={{ color: '#4caf50' }} />;
      default: return <PlayIcon />;
    }
  };

  const getTransactionColor = (type) => {
    switch (type) {
      case 'deduction': return '#f44336';
      case 'purchase': return '#4caf50';
      case 'refill': return '#2196f3';
      case 'addition': return '#4caf50';
      default: return '#666';
    }
  };

  const exportTransactions = () => {
    if (!transactions.length) return;

    const csv = [
      ['Date', 'Type', 'Amount', 'Balance Before', 'Balance After', 'Reason', 'Music', 'Artist'],
      ...transactions.map(t => [
        format(new Date(t.createdAt), 'yyyy-MM-dd HH:mm:ss'),
        t.transactionType,
        t.amount,
        t.balanceBefore,
        t.balanceAfter,
        t.reason,
        t.metadata?.musicTitle || '',
        t.metadata?.artistName || ''
      ])
    ].map(row => row.join(',')).join('\n');

    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `stream-transactions-${selectedUser.email}-${format(new Date(), 'yyyy-MM-dd')}.csv`;
    a.click();
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" sx={{ mb: 3, color: 'var(--text-primary, #fff)', fontWeight: 700 }}>
        Stream Transaction History
      </Typography>

      {/* User Search */}
      <Paper sx={{ p: 3, mb: 3, bgcolor: 'var(--background-color, #1a1a1a)' }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={6}>
            <Autocomplete
              freeSolo
              options={searchResults}
              getOptionLabel={(option) => option.email || ''}
              onInputChange={(event, newValue) => {
                setUserSearch(newValue);
                searchUsers(newValue);
              }}
              onChange={(event, newValue) => {
                if (newValue) handleUserSelect(newValue);
              }}
              loading={searchLoading}
              renderOption={(props, option) => (
                <Box component="li" {...props}>
                  <Avatar
                    src={option.profileImg?.filePath}
                    sx={{ mr: 2, width: 32, height: 32 }}
                  >
                    {option.firstName?.[0]}
                  </Avatar>
                  <Box>
                    <Typography variant="body2">
                      {option.firstName} {option.lastName}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {option.email}
                    </Typography>
                  </Box>
                </Box>
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Search User by Email"
                  variant="outlined"
                  fullWidth
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {searchLoading ? <CircularProgress size={20} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />
          </Grid>
          {selectedUser && (
            <Grid item xs={12} md={6}>
              <Card sx={{ bgcolor: 'var(--background-color, #2a2a2a)' }}>
                <CardContent>
                  <Typography variant="h6">
                    {selectedUser.firstName} {selectedUser.lastName}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {selectedUser.email}
                  </Typography>
                  <Box sx={{ mt: 1 }}>
                    <Chip
                      label={`Balance: ${currentUserBalance}`}
                      color="primary"
                      sx={{ mr: 1 }}
                    />
                    <Chip
                      label={selectedUser.hasPremium ? 'Premium' : 'Free'}
                      color={selectedUser.hasPremium ? 'success' : 'default'}
                    />
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          )}
        </Grid>
      </Paper>

      {selectedUser && (
        <>
          {/* Filters */}
          <Paper sx={{ p: 2, mb: 3, bgcolor: 'var(--background-color, #1a1a1a)' }}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={3}>
                <FormControl fullWidth size="small">
                  <InputLabel>Transaction Type</InputLabel>
                  <Select
                    value={filters.transactionType}
                    onChange={(e) => handleFilterChange('transactionType', e.target.value)}
                    label="Transaction Type"
                  >
                    <MenuItem value="">All</MenuItem>
                    <MenuItem value="deduction">Deductions</MenuItem>
                    <MenuItem value="purchase">Purchases</MenuItem>
                    <MenuItem value="refill">Refills</MenuItem>
                    <MenuItem value="addition">Additions</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  size="small"
                  type="date"
                  label="Start Date"
                  value={filters.startDate}
                  onChange={(e) => handleFilterChange('startDate', e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  size="small"
                  type="date"
                  label="End Date"
                  value={filters.endDate}
                  onChange={(e) => handleFilterChange('endDate', e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <Stack direction="row" spacing={1}>
                  <Button
                    fullWidth
                    variant="outlined"
                    startIcon={<RefreshIcon />}
                    onClick={() => {
                      fetchTransactions();
                      fetchSummary();
                    }}
                  >
                    Refresh
                  </Button>
                  <Button
                    fullWidth
                    variant="outlined"
                    startIcon={<DownloadIcon />}
                    onClick={exportTransactions}
                    disabled={!transactions.length}
                  >
                    Export
                  </Button>
                </Stack>
              </Grid>
            </Grid>
          </Paper>

          {/* Summary Cards */}
          {summary && (
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={12} md={3}>
                <Card sx={{ bgcolor: 'var(--background-color, #2a2a2a)' }}>
                  <CardContent>
                    <Typography variant="h6" color="primary">
                      Total Deductions
                    </Typography>
                    <Typography variant="h4">
                      {summary.deduction?.totalAmount || 0}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {summary.deduction?.count || 0} plays
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} md={3}>
                <Card sx={{ bgcolor: 'var(--background-color, #2a2a2a)' }}>
                  <CardContent>
                    <Typography variant="h6" color="success.main">
                      Total Purchases
                    </Typography>
                    <Typography variant="h4">
                      {summary.purchase?.totalAmount || 0}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {summary.purchase?.count || 0} purchases
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} md={3}>
                <Card sx={{ bgcolor: 'var(--background-color, #2a2a2a)' }}>
                  <CardContent>
                    <Typography variant="h6" color="info.main">
                      Total Refills
                    </Typography>
                    <Typography variant="h4">
                      {summary.refill?.totalAmount || 0}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {summary.refill?.count || 0} refills
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} md={3}>
                <Card sx={{ bgcolor: 'var(--background-color, #2a2a2a)' }}>
                  <CardContent>
                    <Typography variant="h6" color="warning.main">
                      Current Balance
                    </Typography>
                    <Typography variant="h4">
                      {currentUserBalance}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      streams remaining
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          )}

          {/* Most Played Tracks */}
          {mostPlayed.length > 0 && (
            <Paper sx={{ p: 2, mb: 3, bgcolor: 'var(--background-color, #1a1a1a)' }}>
              <Typography variant="h6" sx={{ mb: 2 }}>
                Most Played Tracks
              </Typography>
              <Grid container spacing={2}>
                {mostPlayed.slice(0, 5).map((track, index) => (
                  <Grid item xs={12} md={2.4} key={track._id}>
                    <Card sx={{ bgcolor: 'var(--background-color, #2a2a2a)' }}>
                      <CardContent>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                          <MusicIcon sx={{ mr: 1, color: '#667eea' }} />
                          <Typography variant="h4">#{index + 1}</Typography>
                        </Box>
                        <Typography variant="body2" noWrap>
                          {track.musicTitle}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" noWrap>
                          {track.artistName}
                        </Typography>
                        <Typography variant="h6" color="primary" sx={{ mt: 1 }}>
                          {track.playCount} plays
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </Paper>
          )}

          {/* Transactions Table */}
          <TableContainer component={Paper} sx={{ bgcolor: 'var(--background-color, #1a1a1a)' }}>
            {loading ? (
              <Box sx={{ p: 5, textAlign: 'center' }}>
                <CircularProgress />
              </Box>
            ) : transactions.length === 0 ? (
              <Box sx={{ p: 5, textAlign: 'center' }}>
                <Typography variant="h6" color="text.secondary">
                  No transactions found
                </Typography>
              </Box>
            ) : (
              <>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Date</TableCell>
                      <TableCell>Type</TableCell>
                      <TableCell align="right">Amount</TableCell>
                      <TableCell align="right">Balance Before</TableCell>
                      <TableCell align="right">Balance After</TableCell>
                      <TableCell>Reason</TableCell>
                      <TableCell>Details</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {transactions.map((transaction) => (
                      <TableRow key={transaction._id}>
                        <TableCell>
                          {format(new Date(transaction.createdAt), 'MMM dd, yyyy HH:mm')}
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            {getTransactionIcon(transaction.transactionType)}
                            <Typography
                              variant="body2"
                              sx={{ ml: 1, color: getTransactionColor(transaction.transactionType) }}
                            >
                              {transaction.transactionType}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell align="right">
                          <Typography
                            variant="body2"
                            sx={{
                              color: transaction.amount > 0 ? '#4caf50' : '#f44336',
                              fontWeight: 'bold'
                            }}
                          >
                            {transaction.amount > 0 ? '+' : ''}{transaction.amount}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">{transaction.balanceBefore}</TableCell>
                        <TableCell align="right">
                          <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                            {transaction.balanceAfter}
                          </Typography>
                        </TableCell>
                        <TableCell>{transaction.reason}</TableCell>
                        <TableCell>
                          {transaction.metadata?.musicTitle && (
                            <Box>
                              <Typography variant="caption" noWrap sx={{ maxWidth: 200 }}>
                                {transaction.metadata.musicTitle}
                              </Typography>
                              <Typography variant="caption" color="text.secondary" display="block">
                                by {transaction.metadata.artistName}
                              </Typography>
                            </Box>
                          )}
                          {transaction.metadata?.purchaseDetails && (
                            <Typography variant="caption">
                              {transaction.metadata.purchaseDetails.totalCost} {transaction.metadata.purchaseDetails.currencySymbol}
                            </Typography>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                <TablePagination
                  rowsPerPageOptions={[25, 50, 100]}
                  component="div"
                  count={totalCount}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  onPageChange={(e, newPage) => setPage(newPage)}
                  onRowsPerPageChange={(e) => {
                    setRowsPerPage(parseInt(e.target.value, 10));
                    setPage(0);
                  }}
                />
              </>
            )}
          </TableContainer>
        </>
      )}
    </Box>
  );
}